import { evaluatorIsMeanRecommender } from "@engine/round/accessors";
import type { RoundAggregate, RoundMember } from "@engine/round/aggregate";
import type { Evaluator, UserProfiles } from "@lib/types";

// Pure display-name resolution for round members and evaluators. Lives in the
// shared lib (not the client tree) because the To Do rail assembly runs on
// both the client (the funder's drawer) and the server (the admin read of a
// funder's drawer), and the two must produce identical step titles.
// `funder-workspace/utils.ts` re-exports both, so client call sites are
// unaffected.

export function getMemberDisplay({
  member,
  userProfiles,
}: {
  member: RoundMember;
  userProfiles: UserProfiles;
}) {
  const profile = userProfiles[member.id];
  const primaryLabel = profile?.name?.trim() || member.initials;
  const secondaryLabel =
    profile?.name?.trim() && profile.name !== member.initials
      ? member.initials
      : (profile?.email ?? null);

  return {
    primaryLabel,
    secondaryLabel,
  };
}

/**
 * Funder-facing display name for an evaluator: the member's full profile name
 * when available, falling back to initials for anonymized members (via
 * getMemberDisplay) and to the evaluator shortname when no member resolves.
 * The synthetic mean recommender keeps its shortname.
 */
export function getEvaluatorDisplayName({
  round,
  evaluator,
  userProfiles,
}: {
  round: RoundAggregate;
  evaluator: Evaluator;
  userProfiles: UserProfiles;
}): string {
  if (evaluatorIsMeanRecommender(evaluator)) {
    return evaluator.shortname;
  }
  const member = round.members[evaluator.memberId];
  if (member == null) {
    return evaluator.shortname;
  }
  return getMemberDisplay({ member, userProfiles }).primaryLabel;
}
